"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, FileSpreadsheet, TrendingUp, DollarSign, Layers, Download, Loader2, Eye, User } from "lucide-react";
import { OpportunityDetail } from "@/components/opportunities/OpportunityDetail";
import { useAuth } from "@/hooks/useAuth";
import { Opportunity, OpportunityStage, opportunityAPI } from "@/services/opportunityService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ===== EXCEL ===== */
/* Colores de fondo por columna (ARGB para xlsx) */
const COL_COLORS = [
  "FFE8F4FD", // Oportunidad  — azul muy claro
  "FFF0FDF4", // Empresa       — verde muy claro
  "FFFFF7ED", // Responsable   — naranja muy claro
  "FFF5F3FF", // Productos     — violeta muy claro
  "FFECFDF5", // Fecha         — menta muy claro
  "FFFEF9C3", // Valor         — amarillo muy claro
  "FFEFF6FF", // Probabilidad  — azul cielo muy claro
  "FFFDF2F8", // Etapa         — rosa muy claro
];

const exportToExcel = (data: any[], fileName: string) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const range = XLSX.utils.decode_range(ws["!ref"] || "");

  for (let col = range.s.c; col <= range.e.c; col++) {
    const headerCell = XLSX.utils.encode_cell({ r: 0, c: col });
    if (ws[headerCell]) {
      ws[headerCell].s = {
        font: { bold: true, color: { rgb: "1E3A8A" }, sz: 11 },
        fill: { patternType: "solid", fgColor: { rgb: "DBEAFE" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          bottom: { style: "medium", color: { rgb: "2563EB" } },
        },
      };
    }
    // Datos: fondo alternado por columna
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const dataCell = XLSX.utils.encode_cell({ r: row, c: col });
      if (ws[dataCell]) {
        ws[dataCell].s = {
          fill: { patternType: "solid", fgColor: { rgb: COL_COLORS[col % COL_COLORS.length] } },
          alignment: { vertical: "center" },
          border: {
            bottom: { style: "thin", color: { rgb: "E2E8F0" } },
            right: { style: "thin", color: { rgb: "E2E8F0" } },
          },
        };
      }
    }
  }

  // Ancho de columnas — 11 columnas: Oportunidad, Empresa, Responsable, Producto, SKU, Cantidad, Precio Unit., Fecha, Valor, Probabilidad, Etapa
  ws["!cols"] = [
    { wch: 28 }, { wch: 22 }, { wch: 20 }, { wch: 30 },
    { wch: 16 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
    { wch: 18 }, { wch: 14 }, { wch: 18 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Oportunidades");
  saveAs(new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true })]), `${fileName}.xlsx`);
};

// Expande UNA oportunidad en MÚLTIPLES filas — una por producto
const mapOpportunityToRows = (opp: Opportunity, stages: OpportunityStage[], currency: string) => {
  const stage = stages.find((s) => s.id === opp.stage_id);

  const fecha = (opp as any).estimated_close_date
    ? new Date((opp as any).estimated_close_date).toLocaleDateString("es-GT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    })
    : "—";

  const empresa = typeof opp.client_company === "string"
    ? opp.client_company : (opp.client_company as any)?.name || "—";
  const responsable = typeof opp.contact === "string"
    ? opp.contact : (opp.contact as any)?.name || "—";
  const valor = `${currency} ${Number(opp.estimated_value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  const probabilidad = `${opp.success_probability || 0}%`;
  const etapa = stage?.name || "Sin etapa";

  // Obtener items de la oportunidad
  const items: any[] = Array.isArray((opp as any).items) && (opp as any).items.length > 0
    ? (opp as any).items
    : Array.isArray((opp as any).product) && (opp as any).product.length > 0
      ? (opp as any).product
      : [];

  if (items.length === 0) {
    // Sin productos — una sola fila con campos vacíos
    return [{
      Oportunidad: opp.name,
      Empresa: empresa,
      Responsable: responsable,
      Producto: "—",
      SKU: "—",
      Cantidad: "—",
      "Precio Unitario": "—",
      "Fecha Estimada": fecha,
      Valor: valor,
      Probabilidad: probabilidad,
      Etapa: etapa,
    }];
  }

  // Una fila por producto — los datos de la oportunidad se repiten en cada fila
  return items.map((item: any, idx: number) => {
    const productName = item.product?.name || item.product_name || item.name || "—";
    const sku = item.product?.sku || item.sku || "—";
    const qty = item.quantity ?? "—";
    const basePrice = item.base_price != null
      ? `${currency} ${Number(item.base_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "—";

    return {
      // Solo en la primera fila de la oportunidad se ponen los datos generales
      // Las siguientes filas quedan vacías para que se vea agrupado
      Oportunidad: idx === 0 ? opp.name : "",
      Empresa: idx === 0 ? empresa : "",
      Responsable: idx === 0 ? responsable : "",
      Producto: productName,
      SKU: sku,
      Cantidad: qty,
      "Precio Unitario": basePrice,
      "Fecha Estimada": idx === 0 ? fecha : "",
      Valor: idx === 0 ? valor : "",
      Probabilidad: idx === 0 ? probabilidad : "",
      Etapa: idx === 0 ? etapa : "",
    };
  });
};

const getStageStyles = (type?: string) => {
  switch (type) {
    case "start":
      return {
        bar: "bg-blue-500",
        badge: "bg-blue-50 text-blue-700 border-blue-200",
        text: "text-blue-700"
      };
    case "progress":
      return {
        bar: "bg-amber-500",
        badge: "bg-amber-50 text-amber-700 border-amber-200",
        text: "text-amber-700"
      };
    case "closed_won":
      return {
        bar: "bg-emerald-500",
        badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
        text: "text-emerald-700"
      };
    case "closed_lost":
      return {
        bar: "bg-rose-500",
        badge: "bg-rose-50 text-rose-700 border-rose-200",
        text: "text-rose-700"
      };
    default:
      return {
        bar: "bg-slate-400",
        badge: "bg-slate-50 text-slate-600 border-slate-200",
        text: "text-slate-600"
      };
  }
};

const ProbBar = ({ value, type }: { value: number; type?: string }) => {
  const styles = getStageStyles(type);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  return (
    <div className="flex items-center gap-2.5 min-w-[140px]">
      <div className="flex-1 relative h-2.5 rounded-full bg-slate-100 overflow-hidden shadow-inner">
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${styles.bar}`}
          style={{ width: mounted ? `${value}%` : `0%` }}
        />
      </div>
      <span className={`text-xs tabular-nums w-8 text-right font-bold ${styles.text}`} style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
        {value}%
      </span>
    </div>
  );
};

/* ===== PIE 3D ===== */
const Pie3D = ({ data }: { data: { name: string; value: number; count: number }[] }) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total || !data.length) return <div className="flex items-center justify-center h-64 text-slate-300 text-sm">Sin datos suficientes</div>;

  const cx = 220, cy = 155, rx = 170, ry = 75, DEPTH = 55;
  let acc = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const span = (d.value / total) * 2 * Math.PI;
    const r = { ...d, sa: acc, ea: acc + span, i, pct: Math.round((d.value / total) * 100) };
    acc += span; return r;
  });

  const pt = (a: number, ly = 0) => ({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) + ly });
  const topPath = (sa: number, ea: number, lift = 0) => {
    const s = pt(sa, lift), e = pt(ea, lift);
    return `M${cx},${cy + lift} L${s.x},${s.y} A${rx},${ry} 0 ${ea - sa > Math.PI ? 1 : 0} 1 ${e.x},${e.y} Z`;
  };
  const fullSidePath = (sa: number, ea: number, lift = 0) => {
    const nSa = ((sa % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const nEa = ((ea % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const visSa = Math.max(Math.min(nSa, Math.PI), 0);
    const visEa = Math.max(Math.min(nEa > nSa ? nEa : nEa + 2 * Math.PI, Math.PI), 0);
    if (visSa >= visEa) return null;
    const s = pt(visSa, lift), e = pt(visEa, lift);
    const lg = visEa - visSa > Math.PI ? 1 : 0;
    return `M${s.x},${s.y} A${rx},${ry} 0 ${lg} 1 ${e.x},${e.y} L${e.x},${e.y + DEPTH} A${rx},${ry} 0 ${lg} 0 ${s.x},${s.y + DEPTH} Z`;
  };
  const labelPos = (sa: number, ea: number, lift = 0) => {
    const mid = (sa + ea) / 2;
    return { x: cx + rx * 0.58 * Math.cos(mid), y: cy + ry * 0.58 * Math.sin(mid) + lift };
  };
  const hasSide = (sa: number, ea: number) => {
    const nSa = ((sa % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    return nSa + (ea - sa) > Math.PI || nSa < Math.PI;
  };
  const PIE_PAL = [
    { top: "#E879A0", mid: "#DB2777", dark: "#9D174D" },
    { top: "#60C4F0", mid: "#2563EB", dark: "#1E3A8A" },
    { top: "#4ADE80", mid: "#16A34A", dark: "#14532D" },
    { top: "#FBBF24", mid: "#D97706", dark: "#92400E" },
    { top: "#A78BFA", mid: "#7C3AED", dark: "#4C1D95" },
    { top: "#34D399", mid: "#059669", dark: "#064E3B" },
    { top: "#F87171", mid: "#DC2626", dark: "#7F1D1D" },
    { top: "#38BDF8", mid: "#0284C7", dark: "#0C4A6E" },
  ];

  return (
    <div className="flex flex-col xl:flex-row items-center gap-6 w-full">
      <div className="flex-shrink-0 w-full" style={{ maxWidth: 440 }}>
        <svg viewBox="0 0 440 280" style={{ overflow: "visible", width: "100%", height: "auto" }}>
          <defs>
            {PIE_PAL.map((c, i) => (
              <React.Fragment key={i}>
                <radialGradient id={`tg${i}`} cx="40%" cy="35%" r="70%">
                  <stop offset="0%" stopColor={c.top} /><stop offset="70%" stopColor={c.mid} /><stop offset="100%" stopColor={c.dark} />
                </radialGradient>
                <linearGradient id={`sg${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={c.dark} /><stop offset="40%" stopColor={c.mid} /><stop offset="100%" stopColor={c.dark} />
                </linearGradient>
              </React.Fragment>
            ))}
            <filter id="hoverGlow"><feDropShadow dx="0" dy="-6" stdDeviation="8" floodColor="#00000040" /></filter>
          </defs>
          <ellipse cx={cx} cy={cy + DEPTH + 16} rx={rx - 8} ry={20} fill="#00000015" />
          {slices.map((s, i) => {
            if (!hasSide(s.sa, s.ea)) return null;
            const sp = fullSidePath(s.sa, s.ea, 0); // lift = 0
            if (!sp) return null;
            return <path key={`side${i}`} d={sp} fill={`url(#sg${i % PIE_PAL.length})`} opacity={0.95} />;
          })}
          {slices.map((s, i) => {
            const lift = 0; // Static chart
            const lp = labelPos(s.sa, s.ea, lift);
            return (
              <g key={`top${i}`} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
                style={{ cursor: "pointer" }} filter={hovered === i ? "url(#hoverGlow)" : undefined}>
                <path d={topPath(s.sa, s.ea, lift)} fill={`url(#tg${i % PIE_PAL.length})`} stroke="white" strokeWidth={2} />
                {s.pct >= 6 && (
                  <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle"
                    fill="white" fontSize={11} fontWeight="700"
                    style={{ pointerEvents: "none", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.5))" }}>
                    {s.pct}%
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-2 w-full xl:w-56">
        {slices.map((s, i) => {
          return (
            <div key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200"
              style={{ backgroundColor: hovered === i ? "rgba(241,245,249,0.8)" : "transparent", border: `1px solid ${hovered === i ? "#e2e8f0" : "transparent"}` }}>
              <div className="h-3 w-3 rounded-full flex-shrink-0"
                style={{ background: PIE_PAL[i % PIE_PAL.length].mid }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-700 truncate">{s.name}</p>
                <p className="text-[11px] text-slate-500 font-medium">{s.count} opp.</p>
              </div>
              <span className="text-xs font-bold text-slate-900 tabular-nums">{s.pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ===== COMPONENTE PRINCIPAL ===== */
interface Props { opportunities: Opportunity[]; stages: OpportunityStage[]; slug?: string; onView?: (opportunity: Opportunity) => void }

export const OpportunityTable: React.FC<Props> = ({ opportunities = [], stages = [], slug = "", onView }) => {
  const { company, isAdmin } = useAuth();
  const currency = (company as any)?.currency || "$";
  const [downloading, setDownloading] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // Fetch items then export — always fresh, no duplicates, using optimized endpoint
  const exportOpps = async (key: string, opps: Opportunity[], fileName: string) => {
    setDownloading(key);
    try {
      let enriched: Opportunity[] = [];
      
      if (opps.length > 0) {
        try {
          // Usamos el nuevo endpoint masivo de exportación con la lista de IDs a exportar
          const ids = opps.map((o) => o.id);
          const res = await opportunityAPI.exportOpportunities({ slug, ids });
          enriched = res.data;
        } catch (error) {
          console.error("Error al exportar oportunidades en bloque", error);
          enriched = opps;
        }
      }

      const rows = enriched.flatMap(o => mapOpportunityToRows(o, stages, currency));
      exportToExcel(rows, fileName);
    } finally {
      setDownloading(null);
    }
  };

  const totalValue = opportunities.reduce((s, o) => s + Number(o.estimated_value || 0), 0);
  const avgProbability = opportunities.length
    ? Math.round(opportunities.reduce((s, o) => s + (o.success_probability || 0), 0) / opportunities.length) : 0;

  const stageData = Object.values(
    opportunities.reduce((acc: any, opp) => {
      const stage = stages.find((s) => s.id === opp.stage_id)?.name || "Sin etapa";
      if (!acc[stage]) acc[stage] = { stage, total: 0, count: 0 };
      acc[stage].total += Number(opp.estimated_value || 0); acc[stage].count += 1; return acc;
    }, {})
  ) as { stage: string; total: number; count: number }[];

  const pieData = stageData.map((d) => ({ name: d.stage, value: d.total, count: d.count }));

  const handleDownload = (key: string, fn: () => void) => {
    setDownloading(key);
    setTimeout(() => { fn(); setDownloading(null); }, 600);
  };

  return (
    <div className="space-y-6">
      {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: <Layers className="h-5 w-5 text-blue-500" />, iconBg: "bg-blue-100", label: "Total oportunidades", value: opportunities.length, textColor: "text-slate-900" },
          { icon: <DollarSign className="h-5 w-5 text-emerald-500" />, iconBg: "bg-emerald-100", label: "Valor total", value: `${currency} ${totalValue.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, textColor: "text-slate-900" },
          { icon: <TrendingUp className="h-5 w-5 text-sky-500" />, iconBg: "bg-sky-100", label: "Prob. promedio", value: `${avgProbability}%`, textColor: "text-slate-900" },
        ].map((card, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`h-10 w-10 rounded-xl ${card.iconBg} flex items-center justify-center flex-shrink-0`}>{card.icon}</div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{card.label}</p>
                <p className={`text-xl font-bold ${card.textColor} tabular-nums`} style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{card.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div> */}

      {/* GRÁFICA centrada */}
      {/* 
      <div className="flex justify-center">
        <Card className="w-full max-w-3xl">
          <CardContent className="p-6">
            <p className="text-sm font-semibold text-slate-900 text-center mb-0.5">Distribución por etapa</p>
            <p className="text-xs text-slate-500 text-center mb-5 font-normal">Valor estimado de oportunidades en cada etapa del pipeline</p>
            <Pie3D data={pieData} />
          </CardContent>
        </Card>
      </div>
      */}

      {/* TABLA */}
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-white">
          <div>
            <h3 className="text-base font-semibold text-slate-900 tracking-tight">Listado de oportunidades</h3>
            <p className="text-xs text-slate-500 font-normal mt-0.5">{opportunities.length} registros</p>
          </div>
          <button
            onClick={() => exportOpps("all", opportunities, "Oportunidades")}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
          >
            {downloading === "all" ? <Loader2 size={14} className="animate-spin text-blue-600" /> : <FileSpreadsheet size={14} className="text-blue-600" />}
            {downloading === "all" ? "Exportando..." : "Exportar Excel"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
                <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                  {(() => {
                    const headers = ["Oportunidad", "Empresa"] as string[];
                    if (isAdmin && isAdmin()) headers.push("Responsable");
                    headers.push("Valor", "Probabilidad", "Etapa", "Acciones");
                    return headers.map((h, i) => (
                      <th key={i} className={`px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-600 ${h === "Acciones" ? "text-center" : "text-left"}`}>
                        {h}
                      </th>
                    ));
                  })()}
                </tr>
            </thead>
            <tbody className="bg-white">
              {opportunities.map((opp, idx) => {
                const stage = stages.find((s) => s.id === opp.stage_id);
                const dlKey = `opp-${opp.id}`;
                return (
                  <tr key={opp.id} className="hover:bg-slate-50/60 transition-all duration-200 ease-in-out border-b border-slate-100 last:border-b-0">
                    {/* Nombre — fuente mediana, no muy gruesa */}
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-800 text-sm leading-tight">{opp.name}</p>
                    </td>

                    {/* Empresa */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 border border-slate-200">
                          <Building2 size={14} className="text-slate-500" />
                        </div>
                        <span className="text-sm text-slate-600 font-medium truncate max-w-[140px]">
                          {typeof opp.client_company === "string" ? opp.client_company : opp.client_company?.name}
                        </span>
                      </div>
                    </td>

                    {isAdmin && isAdmin() && (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 border border-slate-200">
                            <User size={14} className="text-slate-500" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium truncate max-w-[140px]">
                            {typeof opp.contact === "string" ? opp.contact : (opp.contact as any)?.name || "—"}
                          </span>
                        </div>
                      </td>
                    )}

                    {/* Valor — font tabular profesional */}
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-slate-800 tabular-nums"
                        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                        {currency} {Number(opp.estimated_value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    {/* Probabilidad — barra fina suave */}
                    <td className="px-6 py-4">
                      <ProbBar value={opp.success_probability || 0} type={stage?.stage_type} />
                    </td>

                    {/* Etapa */}
                    <td className="px-6 py-4">
                      <div className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide inline-flex items-center border ${getStageStyles(stage?.stage_type).badge}`}>
                        {stage?.name || "Sin etapa"}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => { setDetailId(opp.id); setDetailOpen(true); if (onView) onView(opp); }}
                          title="Ver"
                          className="inline-flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-200 hover:bg-slate-100 hover:shadow-sm text-slate-500 hover:text-slate-700 border border-transparent hover:border-slate-300"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => exportOpps(dlKey, [opp], `Oportunidad_${opp.name}`)}
                          title="Descargar"
                          className="inline-flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-200 hover:bg-slate-100 hover:shadow-sm text-slate-500 hover:text-emerald-600 border border-transparent hover:border-slate-300"
                        >
                          {downloading === dlKey ? <Loader2 size={16} className="animate-spin text-blue-600" /> : <FileSpreadsheet size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {opportunities.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-300">
              <Layers size={40} className="mb-3 opacity-20" />
              <p className="text-base font-medium text-slate-400">No hay oportunidades registradas</p>
            </div>
          )}
        </div>
      </Card>
      <OpportunityDetail
        opportunityId={detailId}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        slug={slug}
      />
    </div>
  );
};
